import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import ButtonOutlined from "./StyledComponents/ButtonOutlined";
import httpRequest from "../httpRequest";

// Importing Material-UI Icons
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';


const columns = [
  { id: "id", label: "Scan ID", minWidth: 10 },
  { id: "invoice_number", label: "Document Number", minWidth: 10 },
  { id: "invoice_amount", label: "Invoice Amount", minWidth: 10 },
  { id: "company_name", label: "Company", minWidth: 10 },
  { id: "function_name", label: "Function", minWidth: 10 },
  { id: "status", label: "Status", minWidth: 10 },
];

// Mapping status to Material-UI icons
const statusIcons = {
  Pending: <PendingIcon style={{ marginRight: 8, color: '#e26300' }} />,
  Approved: <CheckCircleIcon style={{ marginRight: 8, color: '#30914c' }} />,
};

const MyInvoicesCard = () => {
  const [invoicesData, setInvoicesData] = useState([]);
  const [vendorsData, setVendorsData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [functionsData, setFunctionsData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoicesResp, vendorsResp, companiesResp, functionsResp] = await Promise.all([
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-invoices?exclude_fields=pdf_file,image_file`),
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-vendors`),
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies`),
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/functions`),
      ]);

      setInvoicesData(invoicesResp.data.invoices);
      setVendorsData(vendorsResp.data.vendors);
      setCompaniesData(companiesResp.data.companies);
      setFunctionsData(functionsResp.data.functions);
    } catch (error) {
      setError("Error fetching data. Please try again later.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const mappedInvoices = useMemo(() => {
    return invoicesData.map((invoice) => {
      const company = companiesData.find((c) => c.id === invoice.company_id);
      const func = functionsData.find((f) => f.id === invoice.function_id);

      let invoice_number = "Unknown Number";
      let invoice_amount = "Unknown Amount";

      try {
        const parsedData = JSON.parse(invoice.parsed_data);
        invoice_number = parsedData["Document Number"] || "Unknown Number";
        invoice_amount = parsedData["Invoice Amount"] || "Unknown Amount";
      } catch (error) {
        console.error("Error parsing invoice data:", error);
      }

      return {
        ...invoice,
        invoice_number,
        invoice_amount,
        company_name: company ? company.company_name : "Unknown Company",
        function_name: func ? func.function_name : "Unknown Function",
      };
    });
  }, [invoicesData, companiesData, functionsData]);

  const topInvoices = useMemo(() => {
    return mappedInvoices
      .sort((a, b) => b.id - a.id)
      .slice(0, 5);
  }, [mappedInvoices]);

  return (
    <Card
      header={<CardHeader avatar={<Icon name="my-view" />} subtitleText="Most Recent Invoices" titleText="My Invoices" />}
    >
      {loading ? (
        <div style={{ textAlign: "center" }}>Loading...</div>
      ) : error ? (
        <div style={{ color: "#e26300", textAlign: "center" }}>{error}</div>
      ) : (
        <>
          <TableContainer style={{ height: 445 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} sx={{ fontWeight: 'bold', fontFamily: 'SapFiori72' }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {topInvoices.map((row) => (
                  <TableRow hover key={row.id}>
                    {columns.map((column) => (
                      <TableCell key={column.id} sx={{ fontFamily: 'SapFiori72' }}>
                        {column.id === "status" ? (
                          <div style={{ display: "flex", alignItems: "center", color: getStatusColor(row[column.id]) }}>
                                                        {statusIcons[row[column.id]] || <ArrowCircleUpIcon style={{ marginRight: 8,  color: '#758ca4' }} />} {/* Default icon if status not found */}
                            {row[column.id]}
                          </div>
                        ) : (
                          row[column.id]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Link to="/my-invoices" style={{ textDecoration: 'none' }}>
              <ButtonOutlined style={{ margin: "10px" }}>
                Show All
              </ButtonOutlined>
            </Link>
          </div>
        </>
      )}
    </Card>
  );
};

// Function to get the color based on status
const getStatusColor = (status) => {
  switch (status) {
    case "Pending":
      return '#e26300';
    case "Approved":
      return '#30914c';
    default:
      return '#758ca4';
  }
};

export default MyInvoicesCard;
